import { useCallback, useRef, useState } from "react";

import { useGesture } from "@use-gesture/react";
import type { Handler } from "@use-gesture/react";

import type { IDragImageRef } from "../components/drag-image";
import { useDashboardContext } from "../contexts/dashboard-context";
import { usePreviewContext } from "../contexts/preview-context";
import { transformPoint } from "../transform";
import { getCenterOfElement } from "../utils/dom";
import { useAppContext } from "./useAppContext";
import { useEditor } from "./useEditor";
import { useIsMobileDevice } from "./useIsMobileDevice";
import { useLogicalSelectedPage } from "./useSelection";
import { useShowDesktopLayout } from "./useShowdesktopLayout";

interface IUsePhotoDragToZoneOptions {
	getContainerElement?: () => HTMLElement | null;
}

export function usePhotoDragToZone(options: IUsePhotoDragToZoneOptions = {}) {
	const { getContainerElement } = options;
	const editor = useEditor();
	const dashboard = useDashboardContext();
	const { googleAnalytics } = useAppContext();
	const page = useLogicalSelectedPage();
	const isMobileDevice = useIsMobileDevice();
	const isDesktopLayout = useShowDesktopLayout();
	const { getZonesAtPoint, previewOverlayRef, getCenterOfZone, getSpreadToScreenTransform } =
		usePreviewContext();
	const dragImageRef = useRef<IDragImageRef>(null);
	const lastHighlightedZoneRef = useRef<eXpress.core.IZone | undefined>(undefined);
	const [isDragging, setIsDragging] = useState(false);

	const applyPhotoToZone = useCallback(
		(photo: eXpress.core.Photo, zone: eXpress.core.PhotoZone) => {
			dashboard.applyPhotoToZone(zone, photo);
		},
		[dashboard],
	);

	const onDrag = useCallback<Handler<"drag">>(
		({ last, args, xy: [x, y], dragging, event }) => {
			const [photo] = (args as [eXpress.core.Photo]) || [];
			const dragImage = dragImageRef.current;

			if (!photo) return;

			if (photo.state === eXpress.core.PhotoState.Rejected) {
				// reset drag when photo was not yet rejected
				const dragImageEl = event?.target as unknown as HTMLImageElement;
				if (!dragImageEl.classList.contains("photo-selection-photo__img")) {
					return;
				}
				const center = getCenterOfElement(dragImageEl);
				if (center) {
					dragImage?.end(center.left - x, center.top - y);
				} else {
					dragImage?.end();
				}
				return;
			}

			const point = { x, y };

			const validDropZone = getZonesAtPoint(
				point,
				(zone) =>
					eXpress.core.isPhotoZone(zone) && dashboard.canApplyPhotoToZone(zone, photo),
			).last() as eXpress.core.PhotoZone | undefined;

			const containerEl = getContainerElement ? getContainerElement() : null;
			const isOutsidePanel = containerEl
				? !document.elementsFromPoint(x, y).includes(containerEl)
				: false;

			if (last) {
				let isApplied = false;

				try {
					// Drag End
					if (validDropZone && isOutsidePanel) {
						applyPhotoToZone(photo, validDropZone);
						// Set the dropZone as current zone (will be selected)
						editor.selection.setCurrentZoneAndBroadcast(validDropZone);

						googleAnalytics.trackEventToGa({
							trackingAction: "PhotoFilledByDrag",
						});
						isApplied = true;
					} else if (page && isOutsidePanel && dashboard.canAddPhotoZoneToPage(page)) {
						const centerPoint = eXpress.math.point(x, y);
						const hoveredPageZone = getZonesAtPoint(
							{ x: centerPoint.left, y: centerPoint.top },
							(zone) => eXpress.core.isPageZone(zone),
						).first();
						if (!hoveredPageZone) return;
						const transform = getSpreadToScreenTransform(
							hoveredPageZone.spread,
							editor.logger,
						)?.inverse();
						if (!transform) return;
						const newPoint = transformPoint(centerPoint, transform);

						const newPhotoZone = dashboard.createPhotoZone(
							hoveredPageZone as eXpress.core.IPageZone,
							newPoint,
							photo,
						);
						isApplied = !!dashboard.addPhotoZoneToPage({
							page: hoveredPageZone as eXpress.core.IPageZone,
							photoZone: newPhotoZone,
						});
						googleAnalytics.trackEventToGa({
							trackingAction: "PhotoZoneAddByDrag",
						});
					}

					// Remove highlighted zone
					previewOverlayRef.current?.updateZoneState(lastHighlightedZoneRef.current, {
						highlightZone: false,
					});
				} finally {
					// Remove DragImage

					const flyToPosition = isApplied
						? getCenterOfZone(validDropZone)
						: getCenterOfElement(event?.target as unknown as HTMLImageElement);

					if (flyToPosition) {
						dragImage?.end(flyToPosition.left - x, flyToPosition.top - y);
					} else {
						dragImage?.end();
					}
				}
			} else if (dragging) {
				// On drag
				// Set URL once
				if (!dragImage?.isDragging()) {
					// Create DragImage
					const thumbUrl = photo.orientedThumbUrl?.getAbsoluteUrl(editor.client);
					if (thumbUrl) {
						// Get element to drag
						const dragImageEl = event?.target as unknown as HTMLImageElement;
						const center = getCenterOfElement(dragImageEl);
						if (center) {
							dragImage?.start(thumbUrl, x, y, center.left - x, center.top - y);
						} else if (thumbUrl) {
							dragImage?.start(thumbUrl, x, y);
						}
					}
				} else {
					dragImage?.moveTo(x, y);
				}

				// Highlight Drag Target
				if (validDropZone !== lastHighlightedZoneRef.current) {
					previewOverlayRef.current?.updateZoneState(lastHighlightedZoneRef.current, {
						highlightZone: false,
					});
					previewOverlayRef.current?.updateZoneState(validDropZone, {
						highlightZone: true,
					});
					lastHighlightedZoneRef.current = validDropZone;
				}
			}
		},
		[
			applyPhotoToZone,
			dashboard,
			getCenterOfZone,
			getContainerElement,
			getSpreadToScreenTransform,
			getZonesAtPoint,
			googleAnalytics,
			editor.client,
			editor.logger,
			editor.selection,
			page,
			previewOverlayRef,
		],
	);

	const getPhotoGestureHandlers = useGesture(
		{
			onDrag,
			onDragStart: () => {
				setIsDragging(true);
			},
			onDragEnd: () => {
				setIsDragging(false);
			},
		},
		{
			drag: {
				pointer: { touch: true },
				preventScrollAxis: isDesktopLayout ? "y" : "x",
				axis: isMobileDevice ? "y" : undefined,
				filterTaps: isMobileDevice ? false : true,
			},
		},
	);

	return {
		getPhotoGestureHandlers,
		isDragging,
		dragImageRef,
	};
}
